import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RelayablyWorkspaceDocument, relayablyWorkspaceDocumentSchema } from '@monorepo/relayably/workspaces'
import { applySession } from '../auth/authSlice'
import { RootState } from '../store'
import { relayablyWorkspaceApi } from './relayablyWorkspaceApi'
import { WorkspaceFieldPayload, WorkspaceResponse } from './types'

type WorkspaceState = {
  status: 'idle' | 'loading' | 'saving' | 'error'
  currentWorkspace: RelayablyWorkspaceDocument | null
  billing: WorkspaceResponse['billing'] | null
  access: WorkspaceResponse['access'] | null
  notifications: WorkspaceResponse['notifications']
  errorMessage: string | null
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends (infer U)[]
    ? U[]
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K]
}

const initialState: WorkspaceState = {
  status: 'idle',
  currentWorkspace: null,
  billing: null,
  access: null,
  notifications: [],
  errorMessage: null,
}

const updateNestedValue = (document: RelayablyWorkspaceDocument, path: string, value: unknown) => {
  const clone = JSON.parse(JSON.stringify(document)) as RelayablyWorkspaceDocument
  const parts = path.split('.')
  let cursor: Record<string, any> = clone as Record<string, any>

  while (parts.length > 1) {
    const part = parts.shift() as string
    cursor[part] = cursor[part] ?? {}
    cursor = cursor[part] as Record<string, any>
  }

  cursor[parts[0]] = value
  return relayablyWorkspaceDocumentSchema.parse(clone)
}

const mergeWorkspace = (base: RelayablyWorkspaceDocument, patch: DeepPartial<RelayablyWorkspaceDocument>) =>
  relayablyWorkspaceDocumentSchema.parse({
    ...base,
    ...patch,
    profile: {
      ...base.profile,
      ...(patch.profile ?? {}),
    },
    workspace: {
      ...base.workspace,
      ...(patch.workspace ?? {}),
      preferredCommunicationStyle: {
        ...base.workspace.preferredCommunicationStyle,
        ...(patch.workspace?.preferredCommunicationStyle ?? {}),
      },
      useCaseFlags: {
        ...base.workspace.useCaseFlags,
        ...(patch.workspace?.useCaseFlags ?? {}),
      },
      templates: patch.workspace?.templates ?? base.workspace.templates,
      agentConfigs: patch.workspace?.agentConfigs ?? base.workspace.agentConfigs,
    },
    onboarding: {
      ...base.onboarding,
      ...(patch.onboarding ?? {}),
    },
  })

export const bootstrapWorkspace = createAsyncThunk('workspace/bootstrap', async (_, thunkApi) => {
  const state = thunkApi.getState() as RootState
  const sessionToken = state.auth.sessionToken
  const user = state.auth.user

  if (!sessionToken || !user) {
    throw new Error('You must be signed in to load a Relayably workspace.')
  }

  return relayablyWorkspaceApi.getCurrentWorkspace(sessionToken, {
    fullName: user.fullName,
    email: user.email,
  })
})

export const saveWorkspace = createAsyncThunk('workspace/save', async (_, thunkApi) => {
  const state = thunkApi.getState() as RootState
  const sessionToken = state.auth.sessionToken
  const currentWorkspace = state.workspace.currentWorkspace

  if (!sessionToken || !currentWorkspace) {
    throw new Error('There is no Relayably workspace draft to save.')
  }

  const response = await relayablyWorkspaceApi.saveWorkspace(sessionToken, currentWorkspace)
  const session = await relayablyWorkspaceApi.refreshSession(sessionToken)
  thunkApi.dispatch(applySession(session))
  return response
})

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    replaceWorkspace(state, action: PayloadAction<RelayablyWorkspaceDocument>) {
      state.currentWorkspace = action.payload
    },
    setWorkspaceMode(state, action: PayloadAction<'structured' | 'conversational' | 'hybrid'>) {
      if (state.currentWorkspace) {
        state.currentWorkspace.onboarding.mode = action.payload
      }
    },
    updateWorkspaceField(state, action: PayloadAction<WorkspaceFieldPayload>) {
      if (!state.currentWorkspace) {
        return
      }

      state.currentWorkspace = updateNestedValue(state.currentWorkspace, action.payload.path, action.payload.value)
    },
    applyWorkspaceDraft(state, action: PayloadAction<DeepPartial<RelayablyWorkspaceDocument>>) {
      if (!state.currentWorkspace) {
        return
      }

      state.currentWorkspace = mergeWorkspace(state.currentWorkspace, action.payload)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(bootstrapWorkspace.pending, state => {
        state.status = 'loading'
        state.errorMessage = null
      })
      .addCase(bootstrapWorkspace.fulfilled, (state, action: PayloadAction<WorkspaceResponse>) => {
        state.status = 'idle'
        state.currentWorkspace = action.payload.workspace
        state.billing = action.payload.billing
        state.access = action.payload.access
        state.notifications = action.payload.notifications
      })
      .addCase(bootstrapWorkspace.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to load your Relayably workspace.'
      })
      .addCase(saveWorkspace.pending, state => {
        state.status = 'saving'
        state.errorMessage = null
      })
      .addCase(saveWorkspace.fulfilled, (state, action: PayloadAction<WorkspaceResponse>) => {
        state.status = 'idle'
        state.currentWorkspace = action.payload.workspace
        state.billing = action.payload.billing
        state.access = action.payload.access
        state.notifications = action.payload.notifications
      })
      .addCase(saveWorkspace.rejected, (state, action) => {
        state.status = 'error'
        state.errorMessage = action.error.message ?? 'Unable to save your Relayably workspace.'
      })
  },
})

export const { applyWorkspaceDraft, replaceWorkspace, setWorkspaceMode, updateWorkspaceField } = workspaceSlice.actions

export const workspaceReducer = workspaceSlice.reducer
